import {
  adminArticleUpdate,
  ArticleStates,
  AdminCommonCategoriesSelect,
} from "@/entities";
import { AdminArticleListButton, AdminArticleRemoveButton } from "@/features";
import { Panel, PanelBody, PanelHeader } from "@/shared";
import { useCommonConfigStore } from "@/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { useRouter, useSearchParams } from "next/navigation";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const Ckeditor = dynamic(() => import("@/shared/admin/ui/editor/ckeditor"), {
  ssr: false,
});

interface AdminArticleFormProps {
  panelTitle: string;
  data: any;
}
export const AdminArticleForm = ({
  panelTitle,
  data,
}: AdminArticleFormProps) => {
  const { commonConfig } = useCommonConfigStore();

  const router = useRouter();
  const searchParams = useSearchParams();

  const { id, created_at, state, common_category_id, title, desc, timestamp } =
    data.result;
  const safeDesc = DOMPurify.sanitize(desc);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      state,
      common_category_id: common_category_id ?? "",
      title: title ?? "",
      desc: safeDesc ?? "",
      created_at: moment(new Date(created_at)).format("YYYY-MM-DD"),
    },
  });

  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: adminArticleUpdate,
    onSuccess: () => {
      toast.success("글정보가 저장되었습니다.");
      queryClient.invalidateQueries({
        queryKey: [
          `admin-articles`,
          { slug: commonConfig.slug, page: 1, perPage: 15 },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [`admin-articles-${id}`],
      });
      router.push(`/admin/${commonConfig.slug}?${searchParams?.toString()}`);
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });
  const onSubmit = async (data: any) => {
    try {
      if (updateMutation.isPending) return;

      await updateMutation.mutateAsync({ id, ...data });
    } catch (e) {
      console.log(e);
      toast.error("에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Panel queryKey={`admin-articles-${id}`}>
          <PanelHeader>{panelTitle}</PanelHeader>
          <PanelBody>
            <div>
              <Controller
                name="state"
                control={control}
                rules={{
                  required: "상태을 입력해주세요.",
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="mb-3">
                    <label className="form-label">상태 *</label>
                    <select
                      className="form-select form-select-md"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                    >
                      {ArticleStates.filter(
                        (item) => item.key !== "delete"
                      ).map((item, index) => {
                        return (
                          <option key={index} value={item.key}>
                            {item.value}
                          </option>
                        );
                      })}
                    </select>
                    {errors.state && (
                      <span className="text-red errorText">
                        {errors.state.message as string}
                      </span>
                    )}
                  </div>
                )}
              />
              {commonConfig.is_category && (
                <Controller
                  name="common_category_id"
                  control={control}
                  rules={{
                    required: "분류를 선택해주세요.",
                  }}
                  render={({ field: { onChange, value } }) => (
                    <div className="mb-3">
                      <label className="form-label">분류 *</label>
                      <AdminCommonCategoriesSelect
                        table={commonConfig.table}
                        slug={commonConfig.slug}
                        defalutValue={{ key: "", value: "분류 선택" }}
                        onChange={onChange}
                        value={value}
                      />
                      {errors.common_category_id && (
                        <span className="text-red errorText">
                          {errors.common_category_id.message as string}
                        </span>
                      )}
                    </div>
                  )}
                />
              )}

              <Controller
                name="title"
                control={control}
                rules={{
                  required: "제목을 입력해주세요.",
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="mb-3">
                    <label className="form-label">제목 *</label>
                    <input
                      type="text"
                      className="form-control form-control-md"
                      title="제목을 입력해주세요."
                      placeholder="제목을 입력해주세요."
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value || ""}
                    />
                    {errors.title && (
                      <span className="text-red errorText">
                        {errors.title.message as string}
                      </span>
                    )}
                  </div>
                )}
              />

              <Controller
                name="desc"
                control={control}
                rules={{
                  required: "설명을 입력해주세요.",
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="mb-3">
                    <label className="form-label">설명 *</label>
                    <Ckeditor
                      onChange={onChange}
                      value={value || ""}
                      table="article"
                      column="desc"
                      timestamp={timestamp}
                    />
                    {errors.desc && (
                      <span className="text-red errorText">
                        {errors.desc.message as string}
                      </span>
                    )}
                  </div>
                )}
              />

              <Controller
                name="created_at"
                control={control}
                rules={{
                  required: "등록일을 입력해주세요.",
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div>
                    <label className="form-label">등록일시 *</label>
                    <div>
                      <DatePicker
                        selected={value as any}
                        onChange={onChange}
                        className="form-control form-control-md"
                        dateFormat={"yyyy-MM-dd"}
                      />
                      {errors.created_at && (
                        <span className="text-red errorText">
                          {errors.created_at.message as string}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
          </PanelBody>
        </Panel>

        <div className="row">
          <div className="col-4 text-left">
            <AdminArticleListButton />
          </div>
          <div className="col-4 text-center">
            <button type="submit" className="btn btn-sm btn-inverse">
              <i className="fa fa-check" />
              <span> 저장</span>
            </button>
          </div>
          <div className="col-4 text-right">
            <AdminArticleRemoveButton />
          </div>
        </div>
      </form>
    </>
  );
};
